function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useCallbackByKey, useMemoByKey } from '@guryou/hooks';
import { stateError, stateIsEmpty, stateIsLoaded, stateIsLoading, stateIsNotInitialized } from '@guryou/redux';
import { generateHashCode } from '@guryou/utils';
import { Paper } from '@material-ui/core';
import Pagination from 'material-ui-flat-pagination';
import PropTypes from 'prop-types';
import { CardBody, EmptyPage, Filters, Modals, TableCellExpandBody, TableRowBody, ToolbarActions } from './Components';
import ContainerHeader from './ContainerHeader';
import { tableRowToColumns } from './utils';
import { CenterMediumContent, FlexRow } from '../content';
import { FullScreenSpinner } from '../spinner';
import Table from '../Table';
export const VIEW_TYPES = {
  CARDS: 1,
  ROWS: 2,
  TABLE: 3
};
const MultipleItemsContainer = /*#__PURE__*/forwardRef((props, ref) => {
  var _filtersContent$props2, _filtersContent$props3;
  const {
    children,
    customClass,
    customKey,
    data,
    downloadFilename,
    entity,
    initialFilter = {},
    noHeader,
    onlyTitle,
    pageSize = 8,
    disableSort,
    showDownloadTool = false,
    showPrintTool = false,
    showAiTool = false,
    onAiClick,
    singleExpandedRow = true,
    title,
    titleAndToolbars = false,
    version,
    extraButton,
    rowsPerPageOptions,
    passingRef = false,
    ComponentToPrint = null,
    paidPackage = false
  } = props;
  const [filteredItems, setFilteredItems] = useState(data);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [localFilter, setLocalFilter] = useState(initialFilter);
  const _children = useMemoByKey(() => React.Children.toArray(children), ["".concat(customKey, "_").concat(version, "_").concat(entity, "_").concat(title)]);
  const filtersContent = _children.find(x => x.type.name === Filters.name);
  const filterChildren = useMemo(() => {
    var _filtersContent$props;
    return filtersContent === null || filtersContent === void 0 ? void 0 : (_filtersContent$props = filtersContent.props) === null || _filtersContent$props === void 0 ? void 0 : _filtersContent$props.children;
  }, [filtersContent]);
  const modalsContent = _children.find(x => x.type.name === Modals.name);
  const toolbarActionsContent = _children.find(x => x.type.name === ToolbarActions.name);
  const cardBody = _children.find(x => x.type.name === CardBody.name);
  const tableRow = _children.find(x => x.type.name === TableRowBody.name);
  const emptyPage = _children.find(x => x.type.name === EmptyPage.name);
  const onFilterChange = filtersContent === null || filtersContent === void 0 ? void 0 : (_filtersContent$props2 = filtersContent.props) === null || _filtersContent$props2 === void 0 ? void 0 : _filtersContent$props2.onChange;
  const onFilterSubmit = filtersContent === null || filtersContent === void 0 ? void 0 : (_filtersContent$props3 = filtersContent.props) === null || _filtersContent$props3 === void 0 ? void 0 : _filtersContent$props3.onSubmit;
  const normalizedEntity = entity === null || entity === void 0 ? void 0 : entity.replace('PROVIDER_', '');
  const onFormChangeCallback = useCallbackByKey(input => {
    disableSort && disableSort(true);
    setLocalFilter(input);
    const items = onFilterChange(input, data);
    setFilteredItems(items);
    setCurrentOffset(0);
  }, [data, disableSort]);
  const onFormSubmitCallback = useCallbackByKey(input => {
    disableSort && disableSort(true);
    setLocalFilter(input);
    onFilterSubmit(input);
    setFilteredItems([]);
  }, [disableSort]);
  const filterFormProps = {};
  if (onFilterSubmit) {
    filterFormProps.onSubmit = onFormSubmitCallback;
  } else {
    filterFormProps.onChange = onFormChangeCallback;
  }
  useEffect(() => {
    let items = onFilterChange ? onFilterChange(localFilter, data) : data;
    if (!disableSort && items) {
      items = items.sort((a, b) => a.orderIndex - b.orderIndex);
    } else if (disableSort && items) {
      items = items.sort((a, b) => b.id - a.id);
    }
    setFilteredItems(items);
  }, [data, localFilter, onFilterChange, disableSort]);
  if ((stateIsLoading(props) || stateIsNotInitialized(props)) && !stateIsLoaded(props)) {
    return /*#__PURE__*/React.createElement(FullScreenSpinner, null);
  }
  if (emptyPage && (stateIsEmpty(props) || stateError(props))) {
    return /*#__PURE__*/React.createElement(CenterMediumContent, {
      className: "text-center"
    }, children.filter(x => x.type.name === EmptyPage.name), modalsContent);
  }
  let mainContent = null;
  if (cardBody) {
    var _filteredItems$slice;
    const cardsContent = filteredItems === null || filteredItems === void 0 ? void 0 : (_filteredItems$slice = filteredItems.slice(currentOffset, currentOffset + pageSize)) === null || _filteredItems$slice === void 0 ? void 0 : _filteredItems$slice.map((item, idx) => cardBody.props.render(item, idx));
    mainContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FlexRow, null, cardsContent), /*#__PURE__*/React.createElement(Pagination, {
      limit: pageSize,
      total: data ? (filteredItems || data).length : [],
      offset: currentOffset,
      onClick: (_, offset) => setCurrentOffset(offset)
    }));
  } else {
    const columns = tableRowToColumns(tableRow);
    const renderExpandableRow = tableRow.props.children.find(x => x.type.name === TableCellExpandBody.name);
    mainContent = /*#__PURE__*/React.createElement(Table, {
      ref: passingRef ? null : ref,
      key: generateHashCode({
        localFilter,
        filteredItems
      }),
      customClass: customClass,
      showDownloadTool: showDownloadTool,
      showPrintTool: showPrintTool,
      customKey: customKey,
      data: filteredItems || [],
      columns: columns,
      toolbars: null,
      renderExpandableRow: renderExpandableRow && renderExpandableRow.props.render,
      downloadFilename: downloadFilename,
      singleExpandedRow: singleExpandedRow,
      setDisableSort: disableSort,
      rowsPerPageOptions: rowsPerPageOptions
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ContainerHeader, _extends({
    ref: ref,
    key: "".concat(title, "_").concat(entity),
    titleAndToolbars: titleAndToolbars,
    noHeader: noHeader,
    onlyTitle: onlyTitle,
    title: title,
    toolbarActionsContent: toolbarActionsContent,
    entity: normalizedEntity,
    showDownloadTool: showDownloadTool,
    showAiTool: showAiTool,
    onAiClick: onAiClick,
    extraButton: extraButton,
    paidPackage: paidPackage
  }, filterFormProps), filterChildren), /*#__PURE__*/React.createElement(Wrapper, {
    cardBody: cardBody
  }, mainContent, modalsContent, ComponentToPrint && /*#__PURE__*/React.cloneElement(ComponentToPrint, {
    printData: filteredItems
  })));
});
const Wrapper = _ref => {
  let {
    children,
    cardBody = false
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, cardBody ? /*#__PURE__*/React.createElement(React.Fragment, null, children) : /*#__PURE__*/React.createElement(Paper, {
    elevation: 2
  }, children));
};
MultipleItemsContainer.propTypes = {
  pageSize: PropTypes.number,
  viewType: PropTypes.number,
  status: PropTypes.number,
  entity: PropTypes.string,
  data: PropTypes.array,
  onAiClick: PropTypes.func,
  singleExpandedRow: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.object
  }))
};
export default MultipleItemsContainer;